import { supabase } from '@/integrations/supabase/client';

export const FREE_QUESTIONS_PER_CATALOG = 30;
export const PRO_QUESTIONS_PER_CATALOG = 'Unlimited';
export const DURATION_MINUTES = 1 * 24 * 60; // 1 day in minutes (for production), change to 5 for testing

// Trial limits for unauthenticated users
export const TRIAL_SENTENCES_LIMIT = 10;
export const TRIAL_QUESTIONS_LIMIT = 10;

interface UserUsageRecord {
  id: string;
  user_id: string;
  catalog_id: string;
  questions_used: number;
  reset_date: string;
  created_at: string;
  updated_at: string;
}

// Helper function to convert database record to interface
const convertToUserUsageRecord = (dbRecord: {
  id: string;
  user_id: string;
  catalog_id: string;
  questions_used: number | null;
  reset_date: string;
  created_at: string | null;
  updated_at: string | null;
}): UserUsageRecord => ({
  id: dbRecord.id,
  user_id: dbRecord.user_id,
  catalog_id: dbRecord.catalog_id,
  questions_used: dbRecord.questions_used || 0,
  reset_date: dbRecord.reset_date,
  created_at: dbRecord.created_at || new Date().toISOString(),
  updated_at: dbRecord.updated_at || new Date().toISOString(),
});

export interface RateLimitResult {
  allowed: boolean;
  remainingQuestions: number;
  resetDate?: Date;
  error?: string;
}

/**
 * Get or create usage record for a user/catalog combination
 */
async function getOrCreateUsageRecord(
  userId: string,
  catalogId: string
): Promise<UserUsageRecord | null> {
  try {
    // First try to get existing record
    const { data: existingRecord, error: fetchError } = await supabase
      .from('user_question_usage')
      .select('*')
      .eq('user_id', userId)
      .eq('catalog_id', catalogId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 = no rows found
      console.error('Error fetching usage record:', fetchError);
      return null;
    }

    if (existingRecord) {
      // Check if record needs to be reset (duration has passed)
      const resetDate = new Date(existingRecord.reset_date);
      if (new Date() > resetDate) {
        // Reset the usage count
        const newResetDate = new Date();
        newResetDate.setMinutes(newResetDate.getMinutes() + DURATION_MINUTES);

        const { data: updatedRecord, error: updateError } = await supabase
          .from('user_question_usage')
          .update({
            questions_used: 0,
            reset_date: newResetDate.toISOString(),
          })
          .eq('id', existingRecord.id)
          .select()
          .single();

        if (updateError) {
          console.error('Error resetting usage record:', updateError);
          return null;
        }

        return convertToUserUsageRecord(updatedRecord);
      }

      return convertToUserUsageRecord(existingRecord);
    }

    // Create new record
    const resetDate = new Date();
    resetDate.setMinutes(resetDate.getMinutes() + DURATION_MINUTES);

    const { data: newRecord, error: insertError } = await supabase
      .from('user_question_usage')
      .insert({
        user_id: userId,
        catalog_id: catalogId,
        questions_used: 0,
        reset_date: resetDate.toISOString(),
      })
      .select()
      .single();

    if (insertError) {
      console.error('Error creating usage record:', insertError);
      return null;
    }

    return convertToUserUsageRecord(newRecord);
  } catch (error) {
    console.error('Error in getOrCreateUsageRecord:', error);
    return null;
  }
}

/**
 * Check if a user can submit a question based on their subscription status
 * @param userId - The user's unique identifier
 * @param hasProAccess - Whether the user has pro subscription
 * @param catalogId - The catalog ID
 * @returns RateLimitResult with allowed status and remaining questions
 */
export async function checkRateLimit(
  userId: string,
  hasProAccess: boolean,
  catalogId: string
): Promise<RateLimitResult> {
  try {
    // Pro users have unlimited access
    if (hasProAccess) {
      return {
        allowed: true,
        remainingQuestions: Infinity,
      };
    }

    // Get usage record for free users
    const usageRecord = await getOrCreateUsageRecord(userId, catalogId);

    if (!usageRecord) {
      return {
        allowed: false,
        remainingQuestions: 0,
        error: 'Unable to check usage limits. Please try again.',
      };
    }

    const questionsUsed = usageRecord.questions_used;
    const remainingQuestions = Math.max(
      0,
      FREE_QUESTIONS_PER_CATALOG - questionsUsed
    );

    return {
      allowed: remainingQuestions > 0,
      remainingQuestions,
      resetDate: new Date(usageRecord.reset_date),
      error:
        remainingQuestions === 0
          ? `You've reached your limit of ${FREE_QUESTIONS_PER_CATALOG} free questions per catalog. Upgrade to Pro for unlimited access.`
          : undefined,
    };
  } catch (error) {
    console.error('Rate limit check failed:', error);
    return {
      allowed: false,
      remainingQuestions: 0,
      error: 'Unable to check usage limits. Please try again.',
    };
  }
}

/**
 * Consume a question for rate limiting (call this when a question is actually submitted)
 * @param userId - The user's unique identifier
 * @param hasProAccess - Whether the user has pro subscription
 * @param catalogId - The catalog ID
 * @returns Promise<boolean> - Success status
 */
export async function consumeQuestion(
  userId: string,
  hasProAccess: boolean,
  catalogId: string
): Promise<boolean> {
  try {
    // Pro users don't consume rate limit
    if (hasProAccess) {
      return true;
    }

    // Get current usage record
    const usageRecord = await getOrCreateUsageRecord(userId, catalogId);

    if (!usageRecord) {
      console.error('Failed to get usage record for consumption');
      return false;
    }

    // Check if user has questions remaining
    if (usageRecord.questions_used >= FREE_QUESTIONS_PER_CATALOG) {
      console.error('User has exceeded question limit');
      return false;
    }

    // Increment usage count
    const { error: updateError } = await supabase
      .from('user_question_usage')
      .update({
        questions_used: usageRecord.questions_used + 1,
      })
      .eq('id', usageRecord.id);

    if (updateError) {
      console.error('Failed to update usage count:', updateError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Failed to consume rate limit:', error);
    return false;
  }
}

/**
 * Get current usage stats for a user
 * @param userId - The user's unique identifier
 * @param hasProAccess - Whether the user has pro subscription
 * @param catalogId - The catalog ID
 * @returns Current usage statistics
 */
export async function getUserUsageStats(
  userId: string,
  hasProAccess: boolean,
  catalogId: string
): Promise<{
  questionsUsed: number;
  questionsRemaining: number;
  resetDate?: Date;
  isPro: boolean;
}> {
  if (hasProAccess) {
    return {
      questionsUsed: 0,
      questionsRemaining: Infinity,
      isPro: true,
    };
  }

  try {
    const usageRecord = await getOrCreateUsageRecord(userId, catalogId);

    if (!usageRecord) {
      return {
        questionsUsed: 0,
        questionsRemaining: 0,
        isPro: false,
      };
    }

    const questionsUsed = usageRecord.questions_used;
    const questionsRemaining = Math.max(
      0,
      FREE_QUESTIONS_PER_CATALOG - questionsUsed
    );

    return {
      questionsUsed,
      questionsRemaining,
      resetDate: new Date(usageRecord.reset_date),
      isPro: false,
    };
  } catch (error) {
    console.error('Failed to get usage stats:', error);
    return {
      questionsUsed: 0,
      questionsRemaining: 0,
      isPro: false,
    };
  }
}
